import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import sellerApi from "@/lib/sellerApi";
import { formatApiError, fileUrl } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Store, Loader2, Upload, Check, ArrowLeft, ArrowRight, X, FileText } from "lucide-react";

const STEPS = ["Account", "Owner", "Shop", "Business", "Shop Info", "Fulfillment", "Documents", "Review"];
const BUSINESS_TYPES = ["Grocery Store", "Supermarket", "Distributor", "Wholesaler", "Manufacturer", "Other"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const CATEGORY_OPTIONS = ["Grocery", "Fruits & Vegetables", "Dairy & Eggs", "Bakery", "Snacks", "Beverages", "Household", "Personal Care", "Other"];

const MOBILE_RE = /^[6-9]\d{9}$/;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PIN_RE = /^\d{6}$/;

const initial = {
  mobile: "", shop_name: "", password: "", confirm_password: "",
  owner_name: "", whatsapp_number: "", email: "", alternate_phone: "",
  business_type: "", address: "", area: "", city: "", district: "", state: "", pincode: "",
  map_link: "", lat: "", lng: "",
  gstin: "", pan: "", fssai: "", business_reg_no: "",
  logo_id: null, photo_ids: [], categories_sold: [], approx_products: "",
  opening_time: "", closing_time: "", working_days: [],
  seller_delivery: false, customer_pickup: false, delivery_radius: "",
  doc_gst_id: null, doc_pan_id: null, doc_fssai_id: null, doc_license_id: null,
  accept_terms: false, accept_privacy: false, confirm_correct: false,
};

function DocUpload({ label, value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await sellerApi.post("/seller/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.file_id);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div className="flex items-center justify-between rounded-xl border border-border bg-cream-alt/40 px-4 py-3">
      <div className="flex items-center gap-2 text-sm text-ink">
        <FileText className="h-4 w-4 text-ink-muted" /> {label}
      </div>
      <div className="flex items-center gap-2">
        {value && <Check className="h-4 w-4 text-sage" />}
        <label className="cursor-pointer">
          <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={pick} data-testid={testid} />
          <span className="inline-flex items-center gap-1 rounded-full border border-ink/15 px-3 py-1 text-xs font-semibold text-ink-soft hover:border-brand">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {value ? "Replace" : "Upload"}
          </span>
        </label>
      </div>
    </div>
  );
}

export default function SellerRegister() {
  usePageMeta("Become a Seller", "Register your shop on NICEE and start selling.");
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleArr = (k, v) => setForm((f) => {
    const arr = f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v];
    return { ...f, [k]: arr };
  });

  const uploadImage = async (file, key, multi = false) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await sellerApi.post("/seller/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (multi) setForm((f) => ({ ...f, [key]: [...f[key], data.file_id] }));
      else setForm((f) => ({ ...f, [key]: data.file_id }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const validateStep = () => {
    if (step === 0) {
      if (!MOBILE_RE.test(form.mobile.trim())) return "Enter a valid 10-digit mobile number";
      if (form.shop_name.trim().length < 2) return "Enter your shop/business name";
      if (form.password.length < 6) return "Password must be at least 6 characters";
      if (form.password !== form.confirm_password) return "Passwords do not match";
    }
    if (step === 1) {
      if (form.owner_name.trim().length < 2) return "Enter the owner's full name";
      if (!MOBILE_RE.test(form.whatsapp_number.trim())) return "Enter a valid 10-digit WhatsApp number";
      if (!EMAIL_RE.test(form.email.trim())) return "Enter a valid email address";
    }
    if (step === 2) {
      if (!form.business_type) return "Select a business type";
      if (form.address.trim().length < 5) return "Enter the complete shop address";
      if (!form.city.trim()) return "Enter your city";
      if (!form.state.trim()) return "Enter your state";
      if (!PIN_RE.test(form.pincode.trim())) return "Enter a valid 6-digit PIN code";
    }
    if (step === 7) {
      if (!form.accept_terms || !form.accept_privacy || !form.confirm_correct)
        return "Please accept the terms and confirm your details";
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
      const { confirm_password, ...payload } = form;
      const { data } = await sellerApi.post("/seller/register", payload);
      toast.success(data.message || "Application submitted");
      navigate("/seller/pending", { replace: true, state: { submitted: true } });
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink px-4 py-8">
      <div className="mx-auto max-w-2xl">
        <div className="mb-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-cream">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand text-ink"><Store className="h-5 w-5" /></span>
            <span className="font-heading text-lg font-bold">NICEE Seller</span>
          </Link>
          <Link to="/seller/login" className="text-sm text-cream/70 hover:text-brand">Have an account? Login</Link>
        </div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl bg-white p-6 shadow-lift sm:p-8">
          {/* Stepper */}
          <div className="mb-6 flex flex-wrap items-center gap-1.5" data-testid="seller-stepper">
            {STEPS.map((s, i) => (
              <div key={s} className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${i === step ? "bg-brand text-ink" : i < step ? "bg-sage/15 text-sage" : "bg-cream-alt text-ink-muted"}`}>
                {i < step ? <Check className="h-3 w-3" /> : <span>{i + 1}</span>} {s}
              </div>
            ))}
          </div>
          <h1 className="font-heading text-xl font-bold text-ink">{STEPS[step]} details</h1>
          <p className="mb-5 text-sm text-ink-muted">Step {step + 1} of {STEPS.length}</p>

          <div className="space-y-4">
            {step === 0 && (
              <>
                <Field label="Mobile Number *"><Input value={form.mobile} onChange={set("mobile")} placeholder="10-digit mobile" className="rounded-xl" data-testid="reg-mobile" /></Field>
                <Field label="Shop / Business Name *"><Input value={form.shop_name} onChange={set("shop_name")} placeholder="e.g. Sri Balaji Supermarket" className="rounded-xl" data-testid="reg-shopname" /></Field>
                <Field label="Password *"><Input type="password" value={form.password} onChange={set("password")} placeholder="At least 6 characters" className="rounded-xl" data-testid="reg-password" /></Field>
                <Field label="Confirm Password *"><Input type="password" value={form.confirm_password} onChange={set("confirm_password")} placeholder="Re-enter password" className="rounded-xl" data-testid="reg-confirm" /></Field>
                <p className="text-xs text-ink-muted">No OTP needed — your mobile number and password are enough to create the account.</p>
              </>
            )}

            {step === 1 && (
              <>
                <Field label="Owner Full Name *"><Input value={form.owner_name} onChange={set("owner_name")} placeholder="Owner's name" className="rounded-xl" data-testid="reg-owner" /></Field>
                <Field label="WhatsApp Number *"><Input value={form.whatsapp_number} onChange={set("whatsapp_number")} placeholder="Orders are sent here" className="rounded-xl" data-testid="reg-whatsapp" /></Field>
                <Field label="Email Address *"><Input type="email" value={form.email} onChange={set("email")} placeholder="shop@email.com" className="rounded-xl" data-testid="reg-email" /></Field>
                <Field label="Alternate Phone (optional)"><Input value={form.alternate_phone} onChange={set("alternate_phone")} placeholder="Optional" className="rounded-xl" data-testid="reg-altphone" /></Field>
              </>
            )}

            {step === 2 && (
              <>
                <Field label="Business Type *">
                  <select value={form.business_type} onChange={set("business_type")} className="h-10 w-full rounded-xl border border-border bg-white px-3 text-sm text-ink outline-none focus:border-brand" data-testid="reg-btype">
                    <option value="">Select type…</option>
                    {BUSINESS_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </Field>
                <Field label="Complete Shop Address *"><Textarea value={form.address} onChange={set("address")} placeholder="Door no, street, landmark" className="rounded-xl" data-testid="reg-address" /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Area / Locality"><Input value={form.area} onChange={set("area")} className="rounded-xl" data-testid="reg-area" /></Field>
                  <Field label="City *"><Input value={form.city} onChange={set("city")} className="rounded-xl" data-testid="reg-city" /></Field>
                  <Field label="District"><Input value={form.district} onChange={set("district")} className="rounded-xl" data-testid="reg-district" /></Field>
                  <Field label="State *"><Input value={form.state} onChange={set("state")} className="rounded-xl" data-testid="reg-state" /></Field>
                  <Field label="PIN Code *"><Input value={form.pincode} onChange={set("pincode")} className="rounded-xl" data-testid="reg-pincode" /></Field>
                </div>
                <Field label="Google Maps Link (optional)"><Input value={form.map_link} onChange={set("map_link")} placeholder="Paste your shop's Google Maps link" className="rounded-xl" data-testid="reg-maplink" /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Latitude (optional)"><Input value={form.lat} onChange={set("lat")} className="rounded-xl" /></Field>
                  <Field label="Longitude (optional)"><Input value={form.lng} onChange={set("lng")} className="rounded-xl" /></Field>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <p className="text-sm text-ink-muted">All fields in this step are optional.</p>
                <Field label="GSTIN"><Input value={form.gstin} onChange={set("gstin")} className="rounded-xl" data-testid="reg-gstin" /></Field>
                <Field label="PAN Number"><Input value={form.pan} onChange={set("pan")} className="rounded-xl" data-testid="reg-pan" /></Field>
                <Field label="FSSAI License Number"><Input value={form.fssai} onChange={set("fssai")} className="rounded-xl" data-testid="reg-fssai" /></Field>
                <Field label="Business Registration Number"><Input value={form.business_reg_no} onChange={set("business_reg_no")} className="rounded-xl" data-testid="reg-regno" /></Field>
              </>
            )}

            {step === 4 && (
              <>
                <Field label="Shop Logo">
                  <div className="flex items-center gap-3">
                    {form.logo_id && <img src={fileUrl(form.logo_id)} alt="logo" className="h-14 w-14 rounded-xl object-cover" />}
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0], "logo_id")} data-testid="reg-logo" />
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 px-4 py-2 text-sm font-semibold text-ink-soft hover:border-brand">
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload logo
                      </span>
                    </label>
                  </div>
                </Field>
                <Field label="Shop Photos">
                  <div className="flex flex-wrap items-center gap-2">
                    {form.photo_ids.map((id) => (
                      <div key={id} className="relative">
                        <img src={fileUrl(id)} alt="shop" className="h-16 w-16 rounded-xl object-cover" />
                        <button type="button" onClick={() => setVal("photo_ids", form.photo_ids.filter((x) => x !== id))} className="absolute -right-1 -top-1 rounded-full bg-ink p-0.5 text-white"><X className="h-3 w-3" /></button>
                      </div>
                    ))}
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0], "photo_ids", true)} data-testid="reg-photos" />
                      <span className="flex h-16 w-16 items-center justify-center rounded-xl border border-dashed border-ink/25 text-ink-muted hover:border-brand"><Upload className="h-5 w-5" /></span>
                    </label>
                  </div>
                </Field>
                <Field label="Product Categories Sold">
                  <div className="flex flex-wrap gap-2">
                    {CATEGORY_OPTIONS.map((c) => (
                      <button type="button" key={c} onClick={() => toggleArr("categories_sold", c)} className={`rounded-full border px-3 py-1 text-xs font-semibold ${form.categories_sold.includes(c) ? "border-brand bg-brand text-ink" : "border-ink/15 text-ink-soft"}`}>{c}</button>
                    ))}
                  </div>
                </Field>
                <Field label="Approximate Number of Products"><Input value={form.approx_products} onChange={set("approx_products")} placeholder="e.g. 200" className="rounded-xl" /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Opening Time"><Input type="time" value={form.opening_time} onChange={set("opening_time")} className="rounded-xl" /></Field>
                  <Field label="Closing Time"><Input type="time" value={form.closing_time} onChange={set("closing_time")} className="rounded-xl" /></Field>
                </div>
                <Field label="Working Days">
                  <div className="flex flex-wrap gap-2">
                    {DAYS.map((d) => (
                      <button type="button" key={d} onClick={() => toggleArr("working_days", d)} className={`rounded-full border px-3 py-1 text-xs font-semibold ${form.working_days.includes(d) ? "border-brand bg-brand text-ink" : "border-ink/15 text-ink-soft"}`}>{d}</button>
                    ))}
                  </div>
                </Field>
              </>
            )}

            {step === 5 && (
              <>
                <div className="flex items-center justify-between rounded-xl bg-cream-alt/50 px-4 py-3">
                  <div><Label>Seller Delivery</Label><p className="text-xs text-ink-muted">You deliver orders to customers</p></div>
                  <Switch checked={form.seller_delivery} onCheckedChange={(v) => setVal("seller_delivery", v)} data-testid="reg-seller-delivery" />
                </div>
                <div className="flex items-center justify-between rounded-xl bg-cream-alt/50 px-4 py-3">
                  <div><Label>Customer Pickup</Label><p className="text-xs text-ink-muted">Customers can collect from your shop</p></div>
                  <Switch checked={form.customer_pickup} onCheckedChange={(v) => setVal("customer_pickup", v)} data-testid="reg-pickup" />
                </div>
                {form.seller_delivery && (
                  <Field label="Delivery Radius (km)"><Input value={form.delivery_radius} onChange={set("delivery_radius")} placeholder="e.g. 5" className="rounded-xl" data-testid="reg-radius" /></Field>
                )}
              </>
            )}

            {step === 6 && (
              <>
                <p className="text-sm text-ink-muted">Documents are optional. Upload what applies to your business (JPG, PNG or PDF).</p>
                <DocUpload label="GST Certificate" value={form.doc_gst_id} onChange={(v) => setVal("doc_gst_id", v)} testid="reg-doc-gst" />
                <DocUpload label="PAN Document" value={form.doc_pan_id} onChange={(v) => setVal("doc_pan_id", v)} testid="reg-doc-pan" />
                <DocUpload label="FSSAI Certificate" value={form.doc_fssai_id} onChange={(v) => setVal("doc_fssai_id", v)} testid="reg-doc-fssai" />
                <DocUpload label="Shop / Business License" value={form.doc_license_id} onChange={(v) => setVal("doc_license_id", v)} testid="reg-doc-license" />
              </>
            )}

            {step === 7 && (
              <>
                <div className="rounded-xl bg-cream-alt/50 p-4 text-sm text-ink-soft">
                  <p><span className="font-semibold text-ink">Shop:</span> {form.shop_name || "—"} ({form.business_type || "—"})</p>
                  <p><span className="font-semibold text-ink">Owner:</span> {form.owner_name || "—"}</p>
                  <p><span className="font-semibold text-ink">WhatsApp:</span> {form.whatsapp_number || "—"}</p>
                  <p><span className="font-semibold text-ink">Location:</span> {[form.city, form.state, form.pincode].filter(Boolean).join(", ") || "—"}</p>
                </div>
                <label className="flex items-start gap-2 text-sm text-ink-soft">
                  <input type="checkbox" checked={form.accept_terms} onChange={(e) => setVal("accept_terms", e.target.checked)} className="mt-1" data-testid="reg-terms" />
                  I accept the NICEE <Link to="/terms" target="_blank" className="text-brand underline">Terms &amp; Conditions</Link>
                </label>
                <label className="flex items-start gap-2 text-sm text-ink-soft">
                  <input type="checkbox" checked={form.accept_privacy} onChange={(e) => setVal("accept_privacy", e.target.checked)} className="mt-1" data-testid="reg-privacy" />
                  I accept the NICEE <Link to="/privacy" target="_blank" className="text-brand underline">Privacy Policy</Link>
                </label>
                <label className="flex items-start gap-2 text-sm text-ink-soft">
                  <input type="checkbox" checked={form.confirm_correct} onChange={(e) => setVal("confirm_correct", e.target.checked)} className="mt-1" data-testid="reg-confirm-correct" />
                  I confirm that the submitted information is correct
                </label>
              </>
            )}
          </div>

          <div className="mt-8 flex items-center justify-between">
            <Button variant="outline" onClick={back} disabled={step === 0} className="rounded-full" data-testid="reg-back">
              <ArrowLeft className="mr-1 h-4 w-4" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={next} className="rounded-full bg-navy text-white hover:bg-navy-hover" data-testid="reg-next">
                Next <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={loading} className="rounded-full bg-brand text-ink hover:bg-brand-hover" data-testid="reg-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Application"}
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-ink-soft">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
